/**
 * Calendar Formatting Utility
 * 
 * Purpose:
 * Pure functions for formatting calendar-related display values, including currency
 * formatting and nights booked display formatting.
 * 
 * High-level Workflow:
 * 1. formatCurrency: Converts a numeric value to a formatted US dollar string
 *    (e.g., 1234.56 becomes "$1,234.56"). Handles null/undefined/0 as "$0.00".
 * 2. formatNightsBooked: Formats a nights booked ratio (guest nights / available nights)
 *    as a visual display with emphasized numerator and de-emphasized denominator.
 *    Returns a React JSX element for inline rendering.
 * 
 * Assumptions & Invariants:
 * - Currency values are in US dollars (USD)
 * - Currency formatting uses en-US locale (US format: $1,234.56)
 * - Null, undefined, or 0 values are displayed as "$0.00" (not empty string)
 * - Nights booked display always shows two numbers separated by "/"
 * - If available nights is 0, displays "0 / 0" (not division by zero error)
 * - Guest nights are rounded to 2 decimal places, available nights are rounded to integer
 * - Visual styling uses CSS variables for consistency with design system
 * 
 * Dependencies:
 * - Internal: None (pure functions, no dependencies)
 * - External: 
 *   - React (for JSX return type in formatNightsBooked)
 *   - JavaScript Intl.NumberFormat API (for currency formatting)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 1 refactoring)
 * - 2026-01-26: Renamed from .js to .jsx to support JSX syntax
 */

import React from 'react';

/**
 * Format a numeric value as US currency
 * 
 * Converts a number to a formatted currency string. Handles edge cases
 * like null, undefined, or zero by returning "$0.00" instead of throwing errors.
 * 
 * @param {number|null|undefined} value - Currency value to format
 * @returns {string} Formatted currency string (e.g., "$1,234.56" or "$0.00")
 */
export function formatCurrency(value) {
  if (value === null || value === undefined || value === 0) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format nights booked as a visual ratio display
 * 
 * Creates a React element that displays guest nights booked vs available nights
 * in a visually emphasized format. The numerator (guest nights) is bold and prominent,
 * while the denominator (available nights) is smaller and de-emphasized.
 * 
 * Example: If 12.5 guest nights are booked out of 30 available nights, displays:
 * "12.5 / 30" where "12.5" is bold and "30" is smaller/lighter.
 * 
 * Special case: If available nights is 0, displays "0 / 0" to avoid division errors.
 * 
 * @param {number} guestNights - Guest nights booked (can be decimal)
 * @param {number} availableNights - Total available nights in the period
 * @returns {JSX.Element} React element with formatted display
 */
export function formatNightsBooked(guestNights, availableNights) {
  // Edge case: if available_nights is 0, display "0 / 0"
  if (availableNights === 0 || availableNights === null || availableNights === undefined) {
    return (
      <span>
        <span style={{ fontWeight: 'var(--font-weight-semibold, 600)' }}>0</span>
        <span style={{ opacity: 0.6, margin: '0 0.25rem' }}>/</span>
        <span style={{ opacity: 0.5, fontSize: '0.9em' }}>0</span>
      </span>
    );
  }
  
  const guestNightsRounded = Math.round(guestNights * 100) / 100;
  const availableNightsRounded = Math.round(availableNights);
  
  // Warn if guest_nights > available_nights (shouldn't happen)
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1') {
    if (guestNightsRounded > availableNightsRounded) {
      console.warn('[CalendarPage] Nights Booked exceeds available nights:', {
        guestNights: guestNightsRounded,
        availableNights: availableNightsRounded,
      });
    }
  }
  
  return (
    <span>
      <span style={{ fontWeight: 'var(--font-weight-semibold, 600)' }}>{guestNightsRounded}</span>
      <span style={{ opacity: 0.6, margin: '0 0.25rem' }}>/</span>
      <span style={{ opacity: 0.5, fontSize: '0.9em' }}>{availableNightsRounded}</span>
    </span>
  );
}
